import * as React from "react";
import { Picker, Item, Flex, Text } from "@adobe/react-spectrum";
import { IFilterControl, IFilterItem } from "../../FilterSetupPlaceholder";
import { OperatorSelector, IOperator } from "../OperatorSelector";
import { AtollonTextField } from "../../../../../../ui/TextField/AtollonTextField";
import { useFilterGuard } from "../../../../../../../hooks/useFilterGuard";
import styles from "./CurrencyFilterControl.module.css";

export interface ICurrencyFilterControlProps {
    filter: IFilterControl;
    onFilterChange: (filter: IFilterControl) => void;
}

const CURRENCY_OPERATORS: IOperator[] = [
    { key: "equals", label: "Equals" },
    { key: "greater_than", label: "Greater Than" },
    { key: "less_than", label: "Less Than" },
    { key: "between", label: "Between" },
    { key: "not_equals", label: "Not Equals" },
    { key: "is_empty", label: "Is Empty" },
    { key: "is_not_empty", label: "Is Not Empty" },
];

const CURRENCY_TYPES = [
    { id: "USD", name: "USD ($)" },
    { id: "EUR", name: "EUR (€)" },
    { id: "GBP", name: "GBP (£)" },
    { id: "JPY", name: "JPY (¥)" },
    { id: "CNY", name: "CNY (¥)" },
];

export const CurrencyFilterControl = React.forwardRef<
    HTMLDivElement,
    ICurrencyFilterControlProps
>((props, forwardedRef) => {
    const { filter, onFilterChange } = props;
    const { notifyChange, syncGuard } = useFilterGuard(onFilterChange);
    const currentFilterItem = filter.filterInfo[0] || {
        name: "value",
        type: "equals",
        value: "",
    };

    const [operator, setOperator] = React.useState<string>(
        currentFilterItem.type || "equals"
    );
    const [value, setValue] = React.useState<number | undefined>(
        currentFilterItem.value
            ? parseFloat(currentFilterItem.value)
            : undefined
    );
    const [fromValue, setFromValue] = React.useState<number | undefined>(
        undefined
    );
    const [toValue, setToValue] = React.useState<number | undefined>(undefined);
    const [currencyType, setCurrencyType] = React.useState<string>("USD");

    React.useEffect(() => {
        syncGuard(filter);
    }, [filter, syncGuard]);

    React.useEffect(() => {
        const currentItem = filter.filterInfo[0];
        if (currentItem) {
            setOperator(currentItem.type || "equals");
            if (currentItem.type === "between") {
                try {
                    const parsed = JSON.parse(currentItem.value || "{}");
                    setFromValue(parsed.from);
                    setToValue(parsed.to);
                    setCurrencyType(parsed.currencyType || "USD");
                } catch {
                    setFromValue(undefined);
                    setToValue(undefined);
                }
            } else if (
                currentItem.type === "is_empty" ||
                currentItem.type === "is_not_empty"
            ) {
                // For is_empty and is_not_empty, value should be empty string
                setValue(undefined);
            } else {
                setValue(
                    currentItem.value
                        ? parseFloat(currentItem.value)
                        : undefined
                );
            }
        }
    }, [filter.filterInfo]);

    const updateFilterInfo = React.useCallback(
        (
            op: string,
            val?: number | string,
            from?: number,
            to?: number,
            currency?: string
        ) => {
            let filterValue: string;
            if (op === "is_empty" || op === "is_not_empty") {
                filterValue = "";
            } else if (op === "between") {
                filterValue = JSON.stringify({
                    from: from ?? "",
                    to: to ?? "",
                    currencyType: currency || currencyType,
                });
            } else {
                filterValue = val?.toString() || "";
            }

            const updatedFilterInfo: IFilterItem[] = [
                {
                    ...currentFilterItem,
                    type: op,
                    value: filterValue,
                },
            ];
            notifyChange({
                ...filter,
                filterInfo: updatedFilterInfo,
            });
        },
        [filter, currentFilterItem, currencyType, notifyChange]
    );

    const handleOperatorChange = React.useCallback(
        (newOperator: string) => {
            setOperator(newOperator);
            // For is_empty and is_not_empty operators, set value to empty string
            if (newOperator === "is_empty" || newOperator === "is_not_empty") {
                updateFilterInfo(newOperator, "");
            } else if (newOperator === "between") {
                updateFilterInfo(
                    newOperator,
                    undefined,
                    fromValue,
                    toValue,
                    currencyType
                );
            } else {
                updateFilterInfo(
                    newOperator,
                    value,
                    undefined,
                    undefined,
                    currencyType
                );
            }
        },
        [value, fromValue, toValue, currencyType, updateFilterInfo]
    );

    const handleValueChange = React.useCallback(
        (e: React.ChangeEvent<HTMLInputElement>) => {
            const newValue = e.target.value
                ? parseFloat(e.target.value)
                : undefined;
            setValue(newValue);
            updateFilterInfo(
                operator,
                newValue,
                undefined,
                undefined,
                currencyType
            );
        },
        [operator, currencyType, updateFilterInfo]
    );

    const handleFromValueChange = React.useCallback(
        (e: React.ChangeEvent<HTMLInputElement>) => {
            const newValue = e.target.value
                ? parseFloat(e.target.value)
                : undefined;
            setFromValue(newValue);
            updateFilterInfo(
                operator,
                undefined,
                newValue,
                toValue,
                currencyType
            );
        },
        [operator, toValue, currencyType, updateFilterInfo]
    );

    const handleToValueChange = React.useCallback(
        (e: React.ChangeEvent<HTMLInputElement>) => {
            const newValue = e.target.value
                ? parseFloat(e.target.value)
                : undefined;
            setToValue(newValue);
            updateFilterInfo(
                operator,
                undefined,
                fromValue,
                newValue,
                currencyType
            );
        },
        [operator, fromValue, currencyType, updateFilterInfo]
    );

    const handleCurrencyChange = React.useCallback(
        (newCurrency: string) => {
            setCurrencyType(newCurrency);
            if (operator === "between") {
                updateFilterInfo(
                    operator,
                    undefined,
                    fromValue,
                    toValue,
                    newCurrency
                );
            } else {
                updateFilterInfo(
                    operator,
                    value,
                    undefined,
                    undefined,
                    newCurrency
                );
            }
        },
        [operator, fromValue, toValue, value, updateFilterInfo]
    );

    return (
        <div ref={forwardedRef} className={styles.currencyFilterControl}>
            <Flex direction="column" gap="size-200">
                {/* Operator row: Label + Selector */}
                <Flex
                    direction="row"
                    alignItems="center"
                    justifyContent="space-between"
                    gap="size-200"
                    UNSAFE_className={styles.operatorRow}
                >
                    <Text UNSAFE_className={styles.operatorLabel}>
                        {filter.label || filter.name}
                    </Text>
                    <div className={styles.operatorSelectorWrapper}>
                        <OperatorSelector
                            operators={CURRENCY_OPERATORS}
                            selectedOperator={operator}
                            onOperatorChange={handleOperatorChange}
                            aria-label="Currency filter operator"
                        />
                    </div>
                </Flex>

                {/* Currency selector and value input row(s) */}
                {operator !== "is_empty" && operator !== "is_not_empty" && (
                    <>
                        <div className={styles.currencyTypeSelector}>
                            <Picker
                                selectedKey={currencyType}
                                onSelectionChange={(key) =>
                                    handleCurrencyChange(key as string)
                                }
                                width="100%"
                                aria-label="Currency type"
                            >
                                {CURRENCY_TYPES.map((currency) => (
                                    <Item key={currency.id}>
                                        {currency.name}
                                    </Item>
                                ))}
                            </Picker>
                        </div>
                        {operator === "between" ? (
                            <div className={styles.rangeInputs}>
                                <AtollonTextField
                                    value={fromValue?.toString() || ""}
                                    onChange={handleFromValueChange}
                                    aria-label={`${
                                        filter.label || filter.name
                                    } filter from value`}
                                    width="100%"
                                />
                                <AtollonTextField
                                    value={toValue?.toString() || ""}
                                    onChange={handleToValueChange}
                                    aria-label={`${
                                        filter.label || filter.name
                                    } filter to value`}
                                    width="100%"
                                />
                            </div>
                        ) : (
                            <div className={styles.valueInput}>
                                <AtollonTextField
                                    value={value?.toString() || ""}
                                    onChange={handleValueChange}
                                    aria-label={`${
                                        filter.label || filter.name
                                    } filter value`}
                                    width="100%"
                                />
                            </div>
                        )}
                    </>
                )}
            </Flex>
        </div>
    );
});

CurrencyFilterControl.displayName = "CurrencyFilterControl";
